import Addon from '@shared/classes/addon';
import Category from '@shared/classes/category';
import DateUtils from '@shared/utils/date';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import MainProps from '@shared/types/props/main';
import Rail from '@shared/classes/rail';
import Setting from '@shared/classes/setting';
import goToContent from '@shared/actions/content/goToContent';
import useFilter from '@components/templates/main/lib/useFilter';
import useMainConfig from '@components/templates/main/lib/useMainConfig';
import useMainData from '@components/templates/main/lib/useMainData';
import useMainKeyboard from '@components/templates/main/lib/useMainKeyboard';
import useParamsWriter from '@components/templates/main/lib/useParamsWriter';
import useTimeline from './useTimeline';
import { MAIN_FOCUS_BLACKLIST } from '@shared/types/constants/focusVisibility';
import { MainContextProps } from '@shared/components/utils/context/mainContext';
import { appSelector } from '@shared/store/slices/app.slice';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { updateParams as updateParamsSlice } from '@shared/store/slices/router.slice';
import { MainLayout, MainTimeline } from '@shared/types/interfaces/main';
import {
  hydrateMain,
  resetMain,
  selectCategory,
  selectSelection,
  setAsset,
  setLayout,
  setNavigation,
  setSelection,
} from '@shared/store/slices/main.slice';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export default function useMain(props: MainProps): MainContextProps {
  // Dispatch
  const dispatch = useAppDispatch();

  // Props
  const {
    channels,
    filter,
    rails,
    sliders,
    flags,
    message,
    threshold,
    onPagination,
  } = props;

  // Selectors
  const { status } = useAppSelector(appSelector);
  const { focus, stack } = useAppSelector(focusSelector);
  const category = useAppSelector(selectCategory);
  const selection = useAppSelector(selectSelection); // The id of the currently selected element.
  const currentParams = store.getState().router.params.current; // Fetch from store to prevent rerender

  // State
  const [filteredRails, setFilteredRails] = useState<Rail[]>(
    (currentParams?.filteredRails as Rail[]) || rails || []
  );

  // -------------------------------------------------------------
  // References
  // -------------------------------------------------------------

  const channelsRef = useRef(channels);
  const flagsRef = useRef(flags);
  const filterRef = useRef(filter);
  const hydratedRef = useRef(false);
  const railsRef = useRef(rails);
  const searchRef = useRef(flags?.search || false);
  const slidersRef = useRef({ id: 'sliders', data: sliders });
  const timelineRef = useRef<MainTimeline | null>(null);
  const previousCategoryIdRef = useRef(category?.id);
  const pendingCategoryAssetReset = useRef(false);
  const railsAtPendingRef = useRef(rails);
  channelsRef.current = channels;
  flagsRef.current = flags;
  filterRef.current = filter;
  railsRef.current = rails;
  searchRef.current = flags?.search || false;
  slidersRef.current = { id: 'sliders', data: sliders };

  // -------------------------------------------------------------
  // Params writer
  // -------------------------------------------------------------

  const { writeParamsNow, cancelScheduledParamsWrite, scheduleParamsWrite } =
    useParamsWriter({
      channelsRef,
      filterRef,
      flagsRef,
      railsRef,
      slidersRef,
      timelineRef,
    });

  // -------------------------------------------------------------
  // Filter hook
  // -------------------------------------------------------------

  /**
   * Handles the filter data and updates the layout when the category changes.
   * Use the filterAll flag to filter all rails available.
   */
  if (filter && !flags?.filter?.all) {
    useFilter({
      channels,
      rails,
      category,
    });
  }

  // -------------------------------------------------------------
  // Timeline hook
  // -------------------------------------------------------------

  if (filter && flags?.timeline) {
    timelineRef.current = useTimeline({
      category,
      filter,
      loading: props.flags?.loading,
      rails,
      timelineRef,
      changeAsset,
      scheduleParamsWrite,
    });
  }

  // -------------------------------------------------------------
  // Data & config
  // -------------------------------------------------------------

  const { data, dataRef } = useMainData({
    category,
    channels,
    filter,
    rails,
    sliders,
  });

  const config = useMainConfig({
    channels,
    data,
    filter,
    flags,
    focus,
    message,
    rails,
    selection,
    slidersRef,
    stack,
    status,
    threshold,
    timelineRef,
  });

  // -------------------------------------------------------------
  // Effect hooks
  // -------------------------------------------------------------

  /**
   * Hydrates `main` slice from router params (restores layout/selection/asset on back-nav).
   * Layout effect so the first paint already reflects restored state instead of flashing empty defaults.
   */
  useLayoutEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    dispatch(
      hydrateMain({
        asset: (currentParams?.asset as Event) || undefined,
        category: (currentParams?.category as Category) || undefined,
        layout: (currentParams?.layout as MainLayout) || {},
        selection: (currentParams?.selection as string) || '',
      })
    );
    return () => {
      cancelScheduledParamsWrite();
      dispatch(resetMain());
    };
  }, []);

  /**
   * Restores focus on mount. Layout effect so `menu.slice.collapse`/`menuExpanded`
   * settle from the real restored focus before the first paint, instead of a passive effect
   * correcting it a frame later and visibly animating the rails container.
   */
  useLayoutEffect(() => {
    if (currentParams?.focus) {
      dispatch(setFocus(currentParams.focus as Focus));
    }
  }, []);

  /**
   * Initializes selection once, when data first becomes available.
   */
  useLayoutEffect(() => {
    const { selection: oldSelection } = store.getState().main;
    if (oldSelection) return;
    const newSelection = data.length ? data[0]?.id || '' : oldSelection;
    dispatch(setSelection(newSelection));
    // If not coming from a blacklisted focus, focus the selection
    if (!MAIN_FOCUS_BLACKLIST.includes(focus as number)) {
      focusSelection(newSelection);
    }
  }, [data]);

  /**
   * Resets the asset when the category genuinely changes —.
   */
  useEffect(() => {
    if (category?.id !== previousCategoryIdRef.current) {
      previousCategoryIdRef.current = category?.id;
      pendingCategoryAssetReset.current = true;
      railsAtPendingRef.current = rails;
    }
    if (flags?.timeline || !flags?.complete || flags?.loading) return;
    if (!pendingCategoryAssetReset.current) return;
    if (rails === railsAtPendingRef.current) return; // Not rebuilt yet
    if (flags?.filter?.all && rails?.length) {
      const rail = railsRef.current?.[0];
      if (!rail) return;
      pendingCategoryAssetReset.current = false;
      changeAsset(rail.id, 0);
    }
  }, [category, filter, rails, flags?.complete, flags?.loading]);

  /**
   * Populates/updates the layout when the data changes.
   */
  useEffect(() => {
    const { asset: currentAsset, layout: oldLayout } = store.getState().main;

    // Update layout
    const newLayout: MainLayout = {};
    let previousId: string | null = null;
    data.forEach((item) => {
      const coordinates = {
        ...(slidersRef.current?.id !== item.id
          ? { hIndex: oldLayout?.[item.id]?.hIndex || 0 }
          : {}),
        vIndex: item.position,
        length: item.data?.length ?? 0,
      };
      newLayout[item.id || ''] = {
        ...coordinates,
        pointer: {
          previous: previousId ? previousId : undefined,
          next: undefined,
        },
      };
      if (previousId) {
        newLayout[previousId].pointer.next = item.id;
      }
      previousId = item.id || null;
    });
    dispatch(setLayout(newLayout));

    // Initialize asset
    if (!currentAsset && railsRef.current?.length) {
      const newAsset = railsRef.current[0].data?.[0] as Event;
      dispatch(setAsset(newAsset));
      dispatch(
        updateParamsSlice({
          asset: newAsset,
        })
      );
    }
    // Filter empty rails
    setFilteredRails(rails?.filter((item) => !!item.data?.length) || []);
  }, [data]);

  // -------------------------------------------------------------
  // Callbacks
  // -------------------------------------------------------------

  /**
   * Computes the asset/category/layout-index change but doesn't dispatch —
   * lets callers merge it with other fields into one `setNavigation` call
   * instead of several.
   */
  function resolveNavigationPayload(id: string, index: number) {
    // Limit index to the length of the data
    const rail = railsRef.current?.find((el) => el.id === id);
    const length =
      dataRef.current.find((el) => el.id === id)?.data?.length || 0;
    const newIndex =
      rail?.circular && index > length - 1
        ? 0
        : Math.max(0, Math.min(length - 1, index));
    const resolvedAsset = dataRef.current.find((el) => el.id === id)?.data?.[
      newIndex
    ];
    // Change filter asset
    if (id === filterRef.current?.id) {
      const newCategory =
        (resolvedAsset as Category) ?? store.getState().main.category;
      return { category: newCategory, layoutId: id, layoutIndex: newIndex };
    }
    // Change other assets (sliders are managed by the sliders component)
    if (id !== slidersRef.current?.id) {
      const newAsset = (resolvedAsset as Event) ?? store.getState().main.asset;
      return { asset: newAsset, layoutId: id, layoutIndex: newIndex };
    }
    return {};
  }

  /**
   * Resets every non-filter rail's horizontal focus index back to 0.
   */
  function resetLayout() {
    const { layout: currentLayout } = store.getState().main;
    const resetLayout: MainLayout = {};
    Object.keys(currentLayout).forEach((id) => {
      resetLayout[id] =
        id === filterRef.current?.id
          ? currentLayout[id]
          : { ...currentLayout[id], hIndex: 0, length: 0 };
    });
    dispatch(setLayout(resetLayout));
  }

  function changeAsset(id: string, index: number) {
    const payload = resolveNavigationPayload(id, index);
    if (Object.keys(payload).length) {
      dispatch(setNavigation(payload));
    }
    // Update params
    scheduleParamsWrite();
  }

  const focusSelection = useCallback((id: string) => {
    // Update focus
    if (id === slidersRef.current?.id) {
      dispatch(setNavigation({ selection: id }));
      dispatch(setFocus(Focus.SLIDERS));
    } else if (id === filterRef.current?.id) {
      dispatch(setNavigation({ selection: id }));
      const newFocus = searchRef.current
        ? Focus.SEARCH_FILTER
        : Focus.FILTER_RAIL;
      dispatch(setFocus(newFocus));
    } else {
      if (id === channelsRef.current?.id) {
        dispatch(setFocus(Focus.CHANNELS_RAIL));
      } else if (railsRef.current?.find((el) => el.id === id)) {
        dispatch(setFocus(Focus.RAILS_LIST));
      }
      // Set new asset, if timeline is set and current event exists, focus it
      let newIndex = store.getState().main.layout[id]?.hIndex || 0;
      if (
        flagsRef.current?.timeline &&
        timelineRef.current &&
        DateUtils.isCurrentDate(
          timelineRef.current?.start?.getTime(),
          timelineRef.current?.end?.getTime()
        )
      ) {
        const rail = railsRef.current?.find((rail) => rail.id === id);
        if (rail?.data) {
          const hIndex = rail.data.findIndex((item) => {
            if (DateUtils.isCurrentDate(item?.date?.start, item?.date?.end)) {
              return true;
            }
            return false;
          });
          if (hIndex > -1) newIndex = hIndex;
        }
      }
      // Else, set last focused or default asset, combined with the
      // selection change in one dispatch
      dispatch(
        setNavigation({
          ...resolveNavigationPayload(id, newIndex),
          selection: id,
        })
      );
    }
    // Deferred params update
    scheduleParamsWrite();
  }, []);

  const hoverAsset = useCallback((asset: Addon | Event | Setting) => {
    dispatch(setAsset(asset));
  }, []);

  const selectAsset = useCallback((id: string, index: number) => {
    const resolvedAsset = dataRef.current.find((el) => el.id === id)?.data?.[
      index
    ] as Event;
    if (!(resolvedAsset instanceof Category)) {
      dispatch(
        setNavigation({
          asset: resolvedAsset,
          layoutId: id,
          layoutIndex: index,
        })
      );
      writeParamsNow();
      goToContent(resolvedAsset);
    } else {
      changeAsset(id, index);
    }
  }, []);

  // -------------------------------------------------------------
  // Keyboard
  // -------------------------------------------------------------

  useMainKeyboard({
    channelsRef,
    filterRef,
    flagsRef,
    railsRef,
    searchRef,
    slidersRef,
    timelineRef,
    cancelScheduledParamsWrite,
    changeAsset,
    focusSelection,
    onPagination,
    resetLayout,
    scheduleParamsWrite,
    selectAsset,
  });

  // -------------------------------------------------------------
  // Return values
  // -------------------------------------------------------------

  return useMemo(
    () => ({
      ...props,
      channels: channelsRef.current,
      config,
      loading: flags?.loading,
      rails: filteredRails,
      changeAsset,
      hoverAsset,
      selectAsset,
    }),
    [
      flags,
      filter,
      filteredRails,
      sliders,
      config,
      message,
      threshold,
      hoverAsset,
      selectAsset,
      onPagination,
    ]
  );
}
